import React from 'react';
import './Privacy.css';

const Privacy = () => {
  return (
    <div className="privacy-page">
      {/* HERO */}
      <section className="legal-hero">
        <div className="container">
          <div className="legal-header">
            <div className="section-tag">Legal</div>
            <h1>Privacy Policy</h1>
            <p>How Krishpar Technologies protects your personal data and privacy</p>
          </div>
        </div>
      </section>

      {/* META INFO */}
      <section className="meta-section">
        <div className="container">
          <div className="meta-bar">
            <span><strong>Effective Date:</strong> 01 January 2025</span>
            <span><strong>Last Updated:</strong> 15 April 2025</span>
            <span><strong>Applies to:</strong> All Krishpar Technologies HR Services</span>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="legal-content">
        <div className="container" style={{ maxWidth: '1000px' }}>

          {/* TABLE OF CONTENTS */}
          <div className="toc">
            <h3>Table of Contents</h3>
            <ol>
              <li><a href="#p1">Information We Collect</a></li>
              <li><a href="#p2">How We Use Information</a></li>
              <li><a href="#p3">HR Data Processing</a></li>
              <li><a href="#p4">Data Sharing & Disclosure</a></li>
              <li><a href="#p5">Data Security</a></li>
              <li><a href="#p6">Data Retention</a></li>
              <li><a href="#p7">Your Rights</a></li>
              <li><a href="#p8">Cookies & Tracking</a></li>
              <li><a href="#p9">Third-Party Services</a></li>
              <li><a href="#p10">Contact & Grievances</a></li>
            </ol>
          </div>

          <div className="legal-intro">
            <p>Krishpar Technologies ("we", "our", or "the Company") is an HR services company committed to protecting the privacy and confidentiality of personal data entrusted to us by our clients, candidates, and employees. This Privacy Policy explains how we collect, use, store, and protect your information.</p>
          </div>

          {/* SECTIONS */}
          <div className="section" id="p1">
            <div className="section-header"><div className="section-num">1</div><h2>Information We Collect</h2></div>
            <p>We collect information necessary to deliver our HR services, including:</p>
            <ul>
              <li><strong>Personal Identifiers:</strong> Full name, date of birth, government-issued ID numbers (Aadhaar, PAN, Passport).</li>
              <li><strong>Contact Information:</strong> Email address, phone number, residential and professional address.</li>
              <li><strong>Employment Data:</strong> Resume/CV, work history, qualifications, certifications, references, and background verification results.</li>
              <li><strong>Payroll & Financial Data:</strong> Bank account details, salary structure, PF/ESI numbers, Form 16, tax declarations.</li>
              <li><strong>Performance Data:</strong> Appraisal records, attendance, leave history, disciplinary records (if applicable).</li>
              <li><strong>Sensitive Personal Data:</strong> Health information (for insurance or leave purposes), caste/religion (only where legally required), disability status — collected only with explicit consent.</li>
              <li><strong>Technical Data:</strong> IP address, browser type, cookies, and usage data when you visit our website.</li>
            </ul>
          </div>

          <div className="section" id="p2">
            <div className="section-header"><div className="section-num">2</div><h2>How We Use Information</h2></div>
            <p>Your personal data is used solely for the following purposes:</p>
            <ul>
              <li>Recruitment, placement, and staffing services on behalf of our client companies.</li>
              <li>Payroll processing, statutory compliance (PF, ESI, TDS, PT), and benefits administration.</li>
              <li>Background verification and reference checks with your consent.</li>
              <li>Training & development, onboarding, and employee engagement programs.</li>
              <li>Legal and regulatory compliance under applicable Indian labour laws (e.g., Shops & Establishments Act, Contract Labour Act, POSH Act).</li>
              <li>Improving our website and service delivery.</li>
            </ul>
            <div className="highlight">
              <strong>We do not sell your personal data.</strong> We never sell, rent, or trade personal information to third parties for their marketing purposes.
            </div>
          </div>

          <div className="section" id="p3">
            <div className="section-header"><div className="section-num">3</div><h2>HR Data Processing</h2></div>
            <p>As an HR services provider, we act both as a <strong>Data Controller</strong> (for data we independently decide to collect) and as a <strong>Data Processor</strong> (when processing data on behalf of our client organisations). In both capacities, we comply with applicable data protection laws including the <em>Digital Personal Data Protection Act, 2023 (DPDPA)</em>.</p>
            <p>Client companies who engage our services are responsible for ensuring their employees and candidates are informed about data sharing with Krishpar Technologies.</p>
          </div>

          <div className="section" id="p4">
            <div className="section-header"><div className="section-num">4</div><h2>Data Sharing & Disclosure</h2></div>
            <p>We share personal data only in the following circumstances:</p>
            <ul>
              <li><strong>Client Companies:</strong> Employers who have engaged us for recruitment or managed HR services receive relevant candidate/employee data.</li>
              <li><strong>Service Providers:</strong> Payroll software vendors, background verification agencies, insurance providers — under strict confidentiality agreements.</li>
              <li><strong>Legal Authorities:</strong> When required by law, court order, or regulatory mandate.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger or acquisition, with prior notice to affected individuals.</li>
            </ul>
          </div>

          <div className="section" id="p5">
            <div className="section-header"><div className="section-num">5</div><h2>Data Security</h2></div>
            <p>We implement appropriate technical and organisational measures to protect your data, including:</p>
            <ul>
              <li>Encrypted storage for sensitive data (AES-256).</li>
              <li>Role-based access controls — only authorised personnel access personal data.</li>
              <li>Regular security audits and vulnerability assessments.</li>
              <li>Secure data transmission over HTTPS/TLS.</li>
              <li>Employee data-privacy training and confidentiality agreements.</li>
            </ul>
            <p>While we strive to protect your data, no method of transmission over the internet is 100% secure. We encourage users to report any suspected breach immediately to our data officer.</p>
          </div>

          <div className="section" id="p6">
            <div className="section-header"><div className="section-num">6</div><h2>Data Retention</h2></div>
            <p>We retain personal data for as long as necessary to fulfil the purposes outlined in this policy, or as required by law:</p>
            <ul>
              <li>Candidate data (not placed): 2 years from last interaction.</li>
              <li>Employee records: 7 years post-employment (as required under PF, gratuity, and tax laws).</li>
              <li>Website usage/cookie data: 12 months.</li>
            </ul>
            <p>After the retention period, data is securely deleted or anonymised.</p>
          </div>

          <div className="section" id="p7">
            <div className="section-header"><div className="section-num">7</div><h2>Your Rights</h2></div>
            <p>Under applicable data protection laws, you have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Erasure:</strong> Request deletion of your data where there is no lawful basis to retain it.</li>
              <li><strong>Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time.</li>
              <li><strong>Grievance Redressal:</strong> Lodge a complaint with our Data Protection Officer (DPO) or the Data Protection Board of India.</li>
            </ul>
            <p>To exercise your rights, please write to <a href="mailto:info@krishpar.com" className="highlight-link">info@krishpar.com</a>. We will respond within 30 days.</p>
          </div>

          <div className="section" id="p8">
            <div className="section-header"><div className="section-num">8</div><h2>Cookies & Tracking</h2></div>
            <p>Our website uses cookies to enhance user experience and analyse traffic. Types of cookies we use:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic website functionality.</li>
              <li><strong>Analytics Cookies:</strong> Google Analytics to understand site usage (anonymised).</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and language preferences.</li>
            </ul>
            <p>You can manage or disable cookies through your browser settings. Disabling essential cookies may affect website functionality.</p>
          </div>

          <div className="section" id="p9">
            <div className="section-header"><div className="section-num">9</div><h2>Third-Party Services</h2></div>
            <p>Our website may contain links to third-party websites (job portals, client sites, etc.). We are not responsible for the privacy practices of those websites and encourage you to read their respective privacy policies.</p>
          </div>

          <div className="section" id="p10">
            <div className="section-header"><div className="section-num">10</div><h2>Contact & Grievances</h2></div>
            <p>For any privacy-related queries, concerns, or requests, please contact our Data Protection Officer:</p>
          </div>

          {/* CONTACT */}
          <div className="contact-section-privacy">
            <div className="contact-card-privacy">
              <h3>Data Protection Officer — Krishpar Technologies</h3>
              <p>Reach out to us for any privacy concerns. We are committed to responding within 30 business days.</p>
              <div className="contact-grid">
                <div className="contact-item">
                  <div className="label">Email</div>
                  <a href="mailto:info@krishpar.com">info@krishpar.com</a>
                </div>
                <div className="contact-item">
                  <div className="label">Phone</div>
                  <a href="tel:+918925516133">+91 89255 16133</a>
                </div>
                <div className="contact-item">
                  <div className="label">Address</div>
                  <span>Chennai, Tamil Nadu, India</span>
                </div>
                <div className="contact-item">
                  <div className="label">Response Time</div>
                  <span>Within 30 business days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
